"use client"

import { useEffect, useRef } from "react"
import { useTranslations } from "next-intl"
import { Check, ChevronDown, TrendingUp, ArrowUpDown, Shuffle } from "lucide-react"

// Sort options for the Browse Shops listings. Default is "Most popular"
// (most-viewed shops first); price sorts cover cheap-first and premium-first.
// Labels live in messages/pages/listings.*.json under "sort.<value>".

export type SortMode =
  | "random"
  | "popular"
  | "price_asc"
  | "price_desc"

export const SORT_OPTIONS: Array<{
  value: SortMode
  icon: typeof TrendingUp
}> = [
  // Random is the default: no shop is always first, and a set of shops that
  // were seeded on the same day can't own the top of the page by freshness.
  // Shuffled once per page load (the page holds the order).
  { value: "random",     icon: Shuffle },
  { value: "popular",    icon: TrendingUp },
  { value: "price_asc",  icon: ArrowUpDown },
  { value: "price_desc", icon: ArrowUpDown },
]

interface ListingsSortModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: SortMode
  onChange: (value: SortMode) => void
}

export function ListingsSortModal({ open, onOpenChange, value, onChange }: ListingsSortModalProps) {
  const t = useTranslations("pages.listings.sort")
  const panelRef = useRef<HTMLDivElement | null>(null)

  // Click-outside close
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onOpenChange(false)
      }
    }
    // Defer so the opening click doesn't immediately close it.
    const id = setTimeout(() => document.addEventListener("mousedown", handler), 0)
    return () => {
      clearTimeout(id)
      document.removeEventListener("mousedown", handler)
    }
  }, [open, onOpenChange])

  // ESC to close
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false)
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [open, onOpenChange])

  if (!open) return null

  return (
    <div
      ref={panelRef}
      className="absolute z-50 mt-2 right-0 w-max min-w-[180px] rounded-3xl bg-[rgba(0,0,0,0.92)] backdrop-blur-md shadow-[0_12px_40px_rgba(0,0,0,0.22)] ring-1 ring-white/5 p-1.5 animate-in fade-in zoom-in-95 duration-200 origin-top-right"
    >
      <ul role="listbox" className="space-y-0.5">
        {SORT_OPTIONS.map(opt => {
          const Icon = opt.icon
          const active = opt.value === value
          return (
            <li key={opt.value}>
              <button
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChange(opt.value)
                  onOpenChange(false)
                }}
                className={`w-full flex items-start gap-2.5 px-3 py-2 rounded-2xl text-left transition-colors ${
                  active ? "bg-white/10" : "hover:bg-white/5"
                }`}
              >
                <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${active ? "text-white" : "text-white/60"}`} strokeWidth={1.75} />
                <div className="flex-1 min-w-0">
                  <div className={`text-[12px] font-medium ${active ? "text-white" : "text-white/85"}`}>{t(opt.value)}</div>
                </div>
                {active && <Check className="w-3.5 h-3.5 text-white flex-shrink-0" strokeWidth={2} />}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function SortTrigger({
  value,
  open,
  onClick,
}: {
  value: SortMode
  open: boolean
  onClick: () => void
}) {
  const t = useTranslations("pages.listings.sort")
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="listbox"
      aria-expanded={open}
      className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-normal transition-colors ${
        open ? "bg-foreground text-background" : "bg-muted text-foreground hover:bg-muted/70"
      }`}
    >
      <span className="opacity-60">{t("label")}</span>
      <span className="font-medium">{t(value)}</span>
      <ChevronDown className={`w-2.5 h-2.5 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
  )
}
